<template>
  <div class="current-point-table">
    <table>
      <thead>
        <tr>
          <th class="col-narrow">Point No.</th>
          <th>Serve</th>
          <th>Receive</th>
          <th>Attacks</th>
          <th>Blocks</th>
          <th>Digs</th>
          <th class="col-narrow">Outcome</th>
          <th>How</th>
          <th>Who</th>
          <th class="col-narrow">P#</th>
          <th class="col-narrow">Rotation</th>
          <th class="col-narrow">Off-court</th>
          <th class="col-narrow">Score</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="col-narrow">{{ currentPoint.id }}</td>
          <td>
            <span v-if="currentPoint.serveAction.player && currentPoint.serveAction.player.number" class="player-number">
              {{ currentPoint.serveAction.player.number }}
            </span>
            {{ currentPoint.serveAction.player && currentPoint.serveAction.player.name ? currentPoint.serveAction.player.name : '' }}
            <template v-if="currentPoint.serveAction.call">
              <span class="entry-badge">X</span>
            </template>
            <template v-else-if="currentPoint.serveAction.rating !== null && currentPoint.serveAction.rating !== undefined">
              <span class="entry-badge" :class="getServeRatingClass(currentPoint.serveAction.rating)">
                {{ currentPoint.serveAction.rating }}
              </span>
            </template>
          </td>
          <td>
            <span v-if="currentPoint.receiveAction.player && currentPoint.receiveAction.player.number" class="player-number">
              {{ currentPoint.receiveAction.player.number }}
            </span>
            <template v-if="currentPoint.receiveAction.call">
              {{ currentPoint.receiveAction.call }}
            </template>
            <template v-else>
              <template v-if="currentPoint.receiveAction.player === 'Team receive error'">Team</template>
              <template v-else>{{ currentPoint.receiveAction.player?.name || '' }}</template>
              <template v-if="currentPoint.receiveAction.rating !== null && currentPoint.receiveAction.rating !== undefined">
                <span class="entry-badge" :class="getReceiveRatingClass(currentPoint.receiveAction.rating)">
                  {{ currentPoint.receiveAction.rating }}
                </span>
              </template>
            </template>
          </td>
          <td>
            <div class="count-list" v-if="attackEntries.length">
              <div v-for="entry in attackEntries" :key="`a-${entry.number}`" class="count-row">
                <div class="count-player">
                  <span class="player-number">{{ entry.number }}</span>
                  <span class="count-name">{{ entry.name }}</span>
                </div>
                <span class="count-value">{{ entry.countLabel }}</span>
              </div>
            </div>
            <span v-else>-</span>
          </td>
          <td>
            <div class="count-list" v-if="blockEntries.length">
              <div v-for="entry in blockEntries" :key="`b-${entry.number}`" class="count-row">
                <div class="count-player">
                  <span class="player-number">{{ entry.number }}</span>
                  <span class="count-name">{{ entry.name }}</span>
                </div>
                <span class="count-value">{{ entry.countLabel }}</span>
              </div>
            </div>
            <span v-else>-</span>
          </td>
          <td>
            <div class="count-list" v-if="digEntries.length">
              <div v-for="entry in digEntries" :key="`d-${entry.number}`" class="count-row">
                <div class="count-player">
                  <span class="player-number">{{ entry.number }}</span>
                  <span class="count-name">{{ entry.name }}</span>
                </div>
                <span class="count-value">{{ entry.countLabel }}</span>
              </div>
            </div>
            <span v-else>-</span>
          </td>
          <td class="col-narrow" :class="getOutcomeClass(currentPoint.scoreAction.outcome)">
            <span class="point-outcome">{{ currentPoint.scoreAction.outcome }}</span>
          </td>
          <td>
            <template v-if="currentPoint.scoreAction.outcome">
              {{ currentPoint.scoreAction.pointDetail }}
            </template>
          </td>
          <td>
            <template v-if="currentPoint.scoreAction.outcome === 'Won'">
              <template v-if="currentPoint.scoreAction.pointDetail === 'Kill block (assist)'">
                <span v-if="currentPoint.scoreAction.player && currentPoint.scoreAction.player.number" class="player-number">
                  {{ currentPoint.scoreAction.player.number }}
                </span>
                {{ currentPoint.scoreAction.player?.name || '' }}
                <span v-if="currentPoint.scoreAction.secondPlayer"> / </span>
                <span v-if="currentPoint.scoreAction.secondPlayer && currentPoint.scoreAction.secondPlayer.number" class="player-number">
                  {{ currentPoint.scoreAction.secondPlayer.number }}
                </span>
                {{ currentPoint.scoreAction.secondPlayer?.name || '' }}
              </template>
              <template v-else>
                <strong v-if="currentPoint.scoreAction.player === 'Team'">Team</strong>
                <span v-else-if="currentPoint.scoreAction.player && currentPoint.scoreAction.player.number" class="player-number">
                  {{ currentPoint.scoreAction.player.number }}
                </span>
                {{ currentPoint.scoreAction.player?.name || '' }}
              </template>
            </template>
            <template v-else-if="currentPoint.scoreAction.outcome === 'Lost'">
              <strong v-if="currentPoint.scoreAction.player === 'Team'">Team</strong>
              <span v-else-if="currentPoint.scoreAction.player && currentPoint.scoreAction.player.number" class="player-number">
                {{ currentPoint.scoreAction.player.number }}
              </span>
              {{ currentPoint.scoreAction.player?.name || '' }}
            </template>
          </td>
          <td class="col-narrow">{{ currentPoint.currentRotationCode }}</td>
          <td class="col-narrow">
            <div class="rotation-grid-mini">
              <span
                v-for="position in getRotationGridPositions(currentPoint.currentOnCourtNumbers)"
                :key="position.position"
                :class="{
                  'setter-circle': isSetter(position.number),
                  'front-row': isFrontRow(position.position),
                  'libero-circle': isLiberoOnCourt(position.number)
                }"
              >
                {{ position.number }}
              </span>
            </div>
          </td>
          <td class="col-narrow">
            <span
              v-if="getOffCourtNumber(currentPoint)"
              class="off-court-circle"
              :class="{ 'libero-off-court': isOffCourtLibero(currentPoint) }"
            >
              {{ getOffCourtNumber(currentPoint) }}
            </span>
            <span v-else>X</span>
          </td>
          <td class="col-narrow">
            <span :class="{ 'score-won': currentPoint.scoreAction.outcome === 'Won' }">
              {{ currentPoint.currentScore.won }}
            </span>
            -
            <span :class="{ 'score-lost': currentPoint.scoreAction.outcome === 'Lost' }">
              {{ currentPoint.currentScore.lost }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'CurrentPointTable',
  props: {
    currentPoint: {
      type: Object,
      required: true
    },
    currentLineup: {
      type: Array,
      required: true
    }
  },
  methods: {
    getCountEntries(bucket = {}) {
      return Object.entries(bucket)
        .map(([number, count]) => ({ number: Number(number), count }))
        .filter(item => item.count > 0)
        .sort((a, b) => a.number - b.number)
        .map(item => ({
          ...item,
          name: this.getPlayerName(item.number),
          countLabel: this.formatCountValue(item.count)
        }));
    },
    getBlockEntries(solo = {}, joint = {}) {
      const merged = { ...solo };
      Object.entries(joint).forEach(([number, count]) => {
        merged[number] = (merged[number] || 0) + count * 0.5;
      });
      return this.getCountEntries(merged);
    },
    formatCountValue(value) {
      return Number.isInteger(value) ? value : value.toFixed(1);
    },
    getPlayerName(number) {
      const rosterMap = this.currentPoint.duringRallyRoster || {};
      if (rosterMap[number]) {
        return rosterMap[number];
      }
      const found = this.currentLineup.find(item => item.player && item.player.number === number);
      return found && found.player ? found.player.name || '' : '';
    },
    getRotationGridPositions(positions) {
      if (!Array.isArray(positions) || positions.length < 6) {
        return [];
      }
      const gridOrder = [4, 3, 2, 5, 6, 1];
      return gridOrder.map(position => ({
        position,
        number: positions[position - 1]
      }));
    },
    isFrontRow(positionId) {
      return [2, 3, 4].includes(positionId);
    },
    isSetter(number) {
      const setterByRoleCode = this.currentLineup.find(pos => pos && pos.roleCode === 'S');
      if (setterByRoleCode && setterByRoleCode.player) {
        return setterByRoleCode.player.number === number;
      }
      const setter = this.currentLineup.find(pos => pos.player && pos.player.role === 'setter');
      return setter && setter.player && setter.player.number === number;
    },
    isLiberoOnCourt(number) {
      return this.currentPoint.currentLiberoNumber && number === this.currentPoint.currentLiberoNumber;
    },
    getOffCourtNumber(point) {
      if (!point || !point.currentLiberoNumber) {
        return '';
      }
      const onCourt = point.currentOnCourtNumbers || [];
      const base = point.currentLineupNumbers || [];
      const liberoIndex = onCourt.findIndex(num => num === point.currentLiberoNumber);
      if (liberoIndex !== -1 && base[liberoIndex]) {
        return base[liberoIndex];
      }
      return point.currentLiberoNumber;
    },
    isOffCourtLibero(point) {
      if (!point || !point.currentLiberoNumber) {
        return false;
      }
      const onCourt = point.currentOnCourtNumbers || [];
      return !onCourt.includes(point.currentLiberoNumber);
    },
    getServeRatingClass(rating) {
      if (rating === 0) {
        return 'badge-error';
      }
      if (rating === 4) {
        return 'badge-success';
      }
      return '';
    },
    getReceiveRatingClass(rating) {
      if (rating === 0) {
        return 'badge-error';
      }
      if (rating === 3) {
        return 'badge-success';
      }
      if (rating === 1) {
        return 'badge-warning';
      }
      return '';
    },
    getOutcomeClass(outcome) {
      return {
        'outcome-won': outcome === 'Won',
        'outcome-lost': outcome === 'Lost'
      };
    }
  },
  computed: {
    attackEntries() {
      return this.getCountEntries(this.currentPoint.duringRally?.attacks || {});
    },
    blockEntries() {
      return this.getBlockEntries(
        this.currentPoint.duringRally?.blocksSolo || {},
        this.currentPoint.duringRally?.blocksJoint || {}
      );
    },
    digEntries() {
      return this.getCountEntries(this.currentPoint.duringRally?.digs || {});
    }
  }
};
</script>

<style scoped lang="scss">
.count-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.count-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.count-player {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.count-name {
  font-weight: 500;
}

.count-value {
  font-weight: 700;
}

.current-point-table {
  overflow: auto;
  margin: 0 20px;
}

table {
  width: 100%;
  table-layout: auto;
  width: max-content;
  min-width: 100%;
}

th, td {
  width: 10%;
  white-space: wrap;
  min-width: 80px;
}

th.col-narrow,
td.col-narrow {
  width: 5%;
}

.rotation-grid-mini {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 2px;
  justify-items: center;
  align-items: center;
  width: max-content;
  height: max-content;
  margin: 0 auto;

  span {
    display: inline-flex;
    width: 18px;
    height: 18px;
    border: 1px solid var(--grey-100);
    border-radius: 40px;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 900;
    margin: 0;
    padding: 0;
  }
}

.rotation-grid-mini span.front-row {
  border-width: 1px;
  border-color: var(--grey-800);
}

.rotation-grid-mini span.setter-circle {
  background-color: var(--grey-800);
  color: var(--white);
}

.rotation-grid-mini span.libero-circle {
  border: 1px solid var(--red-400);
  color: var(--red-400);
}

.off-court-circle {
  display: inline-flex;
  width: 18px;
  height: 18px;
  border: 1px solid var(--grey-50);
  border-radius: 40px;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 900;
}

.off-court-circle.libero-off-court {
  border: 1px solid var(--red-400);
  color: var(--red-400);
}

.entry-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--grey-20);
  color: var(--grey-800);
  font-size: 12px;
  font-weight: 700;
  padding: 0 6px;
  margin-left: 6px;
}

.entry-badge.badge-error {
  background: var(--red-50);
  color: var(--red-600);
}

.entry-badge.badge-success {
  background: var(--green-100);
  color: var(--green-700);
}

.entry-badge.badge-warning {
  background: var(--grey-20);
  color: var(--grey-600);
}

.outcome-won,
.outcome-lost {
  .point-outcome {
    padding: 12px 20px;
    border-radius: 4px;
  }
}

.outcome-won {
  .point-outcome {
    background: var(--green-500);
    color: var(--white);
  }
}

.outcome-lost {
  .point-outcome {
    background: var(--red-300);
    color: var(--white);
  }
}
</style>
